// eslint-disable-next-line
import { cleanArray } from './Array-functions';
import { ensureValueIsInt } from './Number-functions';

/**
 * Checks if the input is an empty object
 *
 * @param {Any} input
 */
export const isEmptyObject = (input: object) => {
  if (!(input instanceof Object)) return false;
  if (input instanceof Array) return false;
  return Object.keys(input).length === 0;
};

/**
 * Checks if the input is an empty array
 *
 * @param {Any} input
 */
export const isEmptyArray = (input: object) => {
  if (!(input instanceof Object)) return false;
  if (!(input instanceof Array)) return false;
  return input.length === 0;
};

/**
 * Cleans the JS object.
 *
 * Any field with empty string or null values are removed from the object. This
 * was developed for cleaning the API request payload.
 *
 * @param {Object} inputObject
 */
export const cleanObject = <T extends object>(
  object: T,
  ensureInt = false,
): T =>
  Object.keys(object).reduce((cleaned, key) => {
    if (
      object[key as keyof object] &&
      typeof object[key as keyof object] === 'object' &&
      !Array.isArray(object[key as keyof object]) &&
      !((object[key as keyof object] as any) instanceof Date)
    ) {
      const newObject = cleanObject(object[key as keyof object], ensureInt);
      if (isEmptyObject(newObject)) return cleaned;
      return {
        ...cleaned,
        [key]: newObject,
      };
    }
    if (
      object[key as keyof object] &&
      typeof object[key as keyof object] === 'object' &&
      Array.isArray(object[key as keyof object])
    ) {
      const newArray = cleanArray(object[key as keyof object], ensureInt);
      if (isEmptyArray(newArray)) return cleaned;
      return {
        ...cleaned,
        [key]: newArray,
      };
    }
    if (
      !((object[key as keyof object] as any) instanceof Date) &&
      (object[key as keyof object] === '' ||
        object[key as keyof object] === null ||
        object[key as keyof object] === undefined ||
        isEmptyObject(object[key as keyof object]) ||
        isEmptyArray(object[key as keyof object]) ||
        Number.isNaN(object[key as keyof object]))
    ) {
      return cleaned;
    }

    const value = object[key as keyof object];

    return {
      ...cleaned,
      [key]: ensureInt ? ensureValueIsInt(value) : value,
    };
  }, {} as T);
